/* Preview + render surface for "The Weather Doesn't Wait" (commercial #2).

   /ad2            — review it
   /ad2?ar=9x16    — the Reels/Stories cut
   /ad2?render=1   — chrome-free stage, clock driven by the renderer (window.__adT)

   Deliberately the same contract as /ad — same stage test id, same body stamp — so one renderer
   films either commercial. */
import { useEffect, useMemo, useRef, useState } from "react";
import StormFilm from "@/components/ad/StormFilm";
import { Play, Pause, RotateCcw, Download, Loader2 } from "lucide-react";

const AR = { "1x1": [1080, 1080], "9x16": [1080, 1920], "16x9": [1920, 1080] };
const SCENE_OF = {
  threat: "threat", problem: "problem", chain: "chain", affect: "automate", trigger: "automate",
  alert: "automate", moving: "automate", hook: "hook", first: "hook", brand: "brand", cta: "brand",
};

export default function StormFilmPage() {
  const params = new URLSearchParams(window.location.search);
  const renderMode = params.get("render") === "1";
  const arKey = AR[params.get("ar")] ? params.get("ar") : "1x1";
  const [frameW, frameH] = AR[arKey];

  const audio = useRef(null);
  const raf = useRef(0);
  const [data, setData] = useState(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    fetch("/ad2/script.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setData({ duration: 45, lines: [] }));
  }, []);

  /* Several spoken lines share one visual beat, so segments are grouped by visual scene. */
  const segments = useMemo(() => {
    const out = [];
    (data?.lines || []).forEach((l) => {
      const scene = SCENE_OF[l.scene] || l.scene;
      const last = out[out.length - 1];
      if (last && last.scene === scene) last.end = l.end;
      else out.push({ scene, start: l.start, end: l.end });
    });
    out.forEach((s, i) => { s.end = i + 1 < out.length ? out[i + 1].start : (data?.duration || s.end); });
    if (out.length) out[0].start = 0;
    return out;
  }, [data]);

  const cue = useMemo(
    () => (data?.lines || []).filter((l) => t >= l.start && t <= l.end + 0.25).slice(-1)[0] || null,
    [data, t]);

  useEffect(() => {
    const fit = () => {
      const availW = window.innerWidth - (renderMode ? 0 : 48);
      const availH = window.innerHeight - (renderMode ? 0 : 150);
      setScale(renderMode ? 1 : Math.min(availW / frameW, availH / frameH, 1));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [frameW, frameH, renderMode]);

  useEffect(() => {
    if (renderMode) return undefined;
    const loop = () => {
      raf.current = requestAnimationFrame(loop);
      const a = audio.current;
      if (a && !a.paused) setT(a.currentTime);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [renderMode]);

  useEffect(() => {
    if (!renderMode) return undefined;
    window.__AD_RENDER__ = true;
    const loop = () => {
      raf.current = requestAnimationFrame(loop);
      const want = window.__adT;
      if (typeof want === "number" && want !== t) setT(want);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [renderMode, t]);

  useEffect(() => {
    if (renderMode) document.body.dataset.adT = t.toFixed(4);
  }, [t, renderMode]);

  const toggle = () => {
    const a = audio.current;
    if (!a) return;
    if (a.paused) {
      if (a.currentTime >= (data?.duration || 45) - 0.1) a.currentTime = 0;
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const restart = () => {
    const a = audio.current;
    if (!a) return;
    a.currentTime = 0;
    setT(0);
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070c1e" }}>
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="storm-page"
      className={`min-h-screen flex flex-col items-center ${renderMode ? "" : "justify-center gap-5 p-6"}`}
      style={{ background: renderMode ? "#070c1e" : "#04060f" }}>
      <div style={{ width: frameW * scale, height: frameH * scale }}>
        <div data-testid="ad-stage"
          style={{ width: frameW, height: frameH, position: "relative", overflow: "hidden",
                   transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <StormFilm t={t} segments={segments} cue={cue} w={frameW} h={frameH} />
        </div>
      </div>

      {!renderMode && (
        <>
          <audio ref={audio} src="/ad2/commercial.mp3" preload="auto" playsInline
            data-testid="storm-audio" onEnded={() => setPlaying(false)} className="hidden" />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button data-testid="storm-play-btn" onClick={toggle}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-gold px-6 font-display
                         text-xs font-bold uppercase tracking-[0.16em] text-royal-deep">
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {playing ? "Pause" : "Play the commercial"}
            </button>
            <button data-testid="storm-restart-btn" onClick={restart}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-white/25
                         px-5 text-xs font-bold uppercase tracking-[0.16em] text-white">
              <RotateCcw className="h-3.5 w-3.5" /> Restart
            </button>
            {Object.keys(AR).map((k) => (
              <a key={k} href={`/ad2?ar=${k}`} data-testid={`storm-ar-${k}`}
                className={`inline-flex h-11 items-center rounded-full px-5 text-xs font-bold
                            uppercase tracking-[0.16em] ${arKey === k
                              ? "bg-white/15 text-white" : "text-blue-100/60 hover:text-white"}`}>
                {k === "1x1" ? "Square" : k === "9x16" ? "Vertical" : "Widescreen"}
              </a>
            ))}
            <a href="/api/public/ad2/square/download" data-testid="storm-download-btn"
              className="inline-flex h-11 items-center gap-2 rounded-full border border-gold/60
                         px-5 text-xs font-bold uppercase tracking-[0.16em] text-gold">
              <Download className="h-3.5 w-3.5" /> Download MP4
            </a>
          </div>
          <p className="text-[11px] text-blue-100/50">
            The Weather Doesn't Wait · {data.duration?.toFixed?.(1) || 45}s · {t.toFixed(1)}s
          </p>
        </>
      )}
    </div>
  );
}
